import { computed, Injectable, inject, signal } from '@angular/core';
import { Observable, of, switchMap, tap, throwError } from 'rxjs';
import { PublicBillSplitApiService } from './public-bill-split-api-service';
import {
  BillSplitSummaryResponseDto,
  CreateBillSplitRequestDto,
  CreateBillSplitResponseDto,
  DistributionMode,
  DraftAssignment,
  DraftItem,
  PublicBillSplitDiscountDraft,
  ReplaceBillSplitItemAssignmentsBulkRequestDto,
  TipType,
} from '@features/client/models/public-bill-split-models';
import { PublicBillSplitDraftStore } from './public-bill-split-draft-store';
import { PublicBillSplitTicketFacade } from './public-bill-split-ticket-facade';
import { TicketParseStrategy } from '@features/client/models/ticket-processing-model';
import { CollaborativeBillSplitApiService } from './collaborative-bill-split-api-service';
import {
  BillSplitJoinPreviewResponseDto,
  ParticipantBillSplitContextResponseDto,
  SaveParticipantProposalsRequestDto,
  SaveParticipantProposalsResponseDto,
  SubmitParticipantProposalsResponseDto,
} from '@features/client/models/public-bill-split-collaboration-models';
import { CollaborativeBillSplitSseService } from './collaborative-bill-split-sse-service';
import { CollaborativeSseEvent } from '@features/client/models/public-bill-split-sse-models';

@Injectable({ providedIn: 'root' })
export class PublicBillSplitFacade {
  private publicApi = inject(PublicBillSplitApiService);
  private collaborativeApi = inject(CollaborativeBillSplitApiService);
  private collaborativeSseService = inject(CollaborativeBillSplitSseService);

  private _summary = signal<BillSplitSummaryResponseDto | null>(null);

  readonly summary = this._summary.asReadonly();

  readonly draft = inject(PublicBillSplitDraftStore);
  readonly ticket = inject(PublicBillSplitTicketFacade);

  readonly splitId = this.draft.splitId;
  readonly step = this.draft.step;
  readonly title = this.draft.title;
  readonly currency = this.draft.currency;
  readonly items = this.draft.items;
  readonly participants = this.draft.participants;
  readonly assignments = this.draft.assignments;
  readonly discounts = this.draft.discounts;

  readonly scanWarning = this.ticket.scanWarning;
  readonly isProcessingScan = this.ticket.isProcessingScan;
  readonly ticketImageUrl = this.ticket.ticketImageUrl;

  readonly saving = signal(false);

  readonly itemsSubtotal = computed(() =>
    this.draft.items().reduce((acc, item) => acc + item.totalPrice, 0),
  );

  readonly totalDiscountAmount = computed(() =>
    this.draft.discounts().reduce((acc, discount) => acc + discount.amount, 0),
  );

  readonly netSubtotal = computed(() =>
    Math.max(0, this.itemsSubtotal() - this.totalDiscountAmount()),
  );

  readonly tipAmount = computed(() => {
    const base = this.netSubtotal();
    const type = this.draft.tipType();
    const value = this.draft.tipValue() ?? 0;

    if (!type) return 0;

    if (type === 'PERCENTAGE') {
      return (base * value) / 100;
    }

    return value;
  });

  readonly totalAmount = computed(() => this.netSubtotal() + this.tipAmount());

  createSplit(hostName?: string): Observable<CreateBillSplitResponseDto> {
    const payload: CreateBillSplitRequestDto = {
      title: this.draft.title(),
      currency: this.draft.currency(),
      hostName: hostName?.trim() || undefined,
    };

    return this.publicApi.createSplit(payload).pipe(
      tap((response) => {
        this.draft.setSplitId(response.id);
        this.storePreview();
      }),
    );
  }

  startPersonalizedSplit(title: string, hostName?: string): Observable<CreateBillSplitResponseDto> {
    this.draft.updateConfig({ title });
    return this.createSplit(hostName);
  }

  getJoinPreview(splitId: string): Observable<BillSplitJoinPreviewResponseDto> {
    return this.collaborativeApi.getJoinPreview(splitId);
  }

  joinSplit(splitId: string, name: string) {
    return this.collaborativeApi.join(splitId, { name });
  }

  refreshSummary(): Observable<BillSplitSummaryResponseDto> {
    const splitId = this.draft.splitId();

    if (!splitId) {
      return throwError(() => new Error('No split id'));
    }

    return this.publicApi.getSummary(splitId).pipe(
      tap((summary) => {
        this._summary.set(summary);
      }),
    );
  }

  saveItems(): Observable<unknown> {
    const splitId = this.draft.splitId();
    const draftItems = this.draft.items();

    if (!splitId) {
      return throwError(() => new Error('No split id'));
    }

    if (!draftItems.length) {
      return of(null);
    }

    return this.publicApi
      .addItemsBulk(splitId, {
        items: draftItems.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        })),
      })
      .pipe(
        tap((response) => {
          const persistedItems = response.items.map((backendItem, index) => {
            const draftItem = draftItems[index];

            return {
              ...draftItem,
              id: backendItem.id,
              backendId: backendItem.id,
              totalPrice: backendItem.totalPrice,
            };
          });

          this.draft.setItems(persistedItems);
          this.draft.setAssignments([]);
          this.storePreview();
        }),
      );
  }

  saveAdjustments(participantIds: string[] = []): Observable<unknown> {
    const splitId = this.draft.splitId();

    if (!splitId) {
      return throwError(() => new Error('No split id'));
    }

    const cleanParticipantIds = participantIds.filter(Boolean);

    const validParticipantIdSet = new Set(cleanParticipantIds);

    const normalizeParticipantIds = (ids: string[] | undefined): string[] => {
      const validIds = (ids ?? []).filter((id) => validParticipantIdSet.has(id));

      return validIds.length > 0 ? validIds : cleanParticipantIds;
    };

    const discountAdjustments = this.draft.discountDistributions().map((dist) => ({
      id: dist.id,
      type: 'DISCOUNT' as const,
      label: dist.label || 'Descuento',
      valueType: 'FIXED_AMOUNT' as const,
      value: dist.amount,
      distributionMode: dist.distributionMode,
      participantIds: cleanParticipantIds.length
        ? normalizeParticipantIds(dist.participantIds)
        : [],
    }));

    const tipAdjustment =
      this.draft.tipType() !== null && (this.draft.tipValue() ?? 0) > 0
        ? [
            {
              type: 'TIP' as const,
              label: 'Propina',
              valueType:
                this.draft.tipType() === 'PERCENTAGE'
                  ? ('PERCENTAGE' as const)
                  : ('FIXED_AMOUNT' as const),
              value: this.draft.tipValue()!,
              distributionMode: this.draft.tipDistributionMode(),
              participantIds: cleanParticipantIds.length
                ? normalizeParticipantIds(this.draft.tipParticipantIds())
                : [],
            },
          ]
        : [];

    return this.publicApi.replaceAdjustments(splitId, {
      adjustments: [...discountAdjustments, ...tipAdjustment],
    });
  }

  saveParticipants(): Observable<unknown> {
    const splitId = this.draft.splitId();
    const participants = this.draft.participants();

    if (!splitId) {
      return throwError(() => new Error('No split id'));
    }

    if (!participants.length) {
      return of(null);
    }

    return this.publicApi
      .addParticipantsBulk(splitId, {
        participants: participants.map((participant) => ({
          name: participant.name,
        })),
      })
      .pipe(tap(() => this.storePreview()));
  }

  saveAssignmentsAndLoadSummary(
    participantIds: string[] = [],
  ): Observable<BillSplitSummaryResponseDto> {
    return this.persistUnsavedSplitItems().pipe(
      switchMap(() => this.saveAssignmentsAndLoadSummaryInternal(participantIds)),
    );
  }

  listenParticipantSplitEvents(
    splitId: string,
    participantToken: string,
  ): Observable<CollaborativeSseEvent> {
    return this.collaborativeSseService.subscribeParticipant(splitId, participantToken);
  }

  getParticipantContext(
    splitId: string,
    participantToken: string,
  ): Observable<ParticipantBillSplitContextResponseDto> {
    return this.collaborativeApi.getParticipantContext(splitId, participantToken);
  }

  submitParticipantSelection(
    splitId: string,
    participantId: string,
    participantToken: string,
  ): Observable<SubmitParticipantProposalsResponseDto> {
    return this.collaborativeApi.submit(splitId, participantId, participantToken);
  }

  saveParticipantSelectionDraft(
    splitId: string,
    participantId: string,
    participantToken: string,
    body: SaveParticipantProposalsRequestDto,
  ): Observable<SaveParticipantProposalsResponseDto> {
    return this.collaborativeApi.saveDraft(splitId, participantId, participantToken, body);
  }

  setDiscounts(discounts: PublicBillSplitDiscountDraft[]): void {
    this.draft.setDiscounts(discounts);
    this.storePreview();
  }

  setItems(items: DraftItem[]): void {
    this.draft.setItems(items);

    const validIds = new Set(items.map((item) => item.id));
    const filteredAssignments = this.draft
      .assignments()
      .filter((assignment) => validIds.has(assignment.itemId));

    this.draft.setAssignments(filteredAssignments);
  }

  setAssignments(assignments: DraftAssignment[]): void {
    this.draft.setAssignments(assignments);
  }

  setTipType(value: TipType | null): void {
    this.draft.tipType.set(value);
  }

  setTipValue(value: number | null): void {
    this.draft.tipValue.set(value);
  }

  setTipDistributionMode(mode: DistributionMode): void {
    this.draft.tipDistributionMode.set(mode);
  }

  setSelectedTicketImage(file: Blob): void {
    this.ticket.setSelectedTicketImage(file);
  }

  processTicket(splitId: string) {
    return this.ticket.processTicket(splitId);
  }

  reprocessTicket(strategy?: TicketParseStrategy) {
    return this.ticket.reprocessTicket(strategy);
  }

  clearScanWarning(): void {
    this.ticket.clearScanWarning();
  }

  hasTipSelected(): boolean {
    return this.draft.tipType() !== null && (this.draft.tipValue() ?? 0) > 0;
  }

  isStorageValid(): boolean {
    return !!this.draft.splitId();
  }

  reset(): void {
    this.clearAll();
  }

  clearAll(): void {
    this.ticket.clear();
    this.draft.clear();
    localStorage.removeItem('dinno_split_preview');
    localStorage.removeItem('dinno_last_update');
  }

  private storePreview(): void {
    const preview = {
      title: this.draft.title(),
      itemsCount: this.draft.items().length,
      participantsCount: this.draft.participants().length,
      totalAmount: this.netSubtotal(),
      lastUpdatedAt: Date.now(),
    };

    localStorage.setItem('dinno_split_preview', JSON.stringify(preview));
    localStorage.setItem('dinno_last_update', String(Date.now()));
  }

  private persistUnsavedSplitItems(): Observable<unknown> {
    const splitId = this.draft.splitId();

    if (!splitId) {
      return throwError(() => new Error('No split id'));
    }

    const unsavedChildren = this.draft
      .items()
      .filter((item) => item.isSplitChild && !item.backendId);

    if (!unsavedChildren.length) {
      return of(null);
    }

    const firstChild = unsavedChildren[0];

    if (!firstChild.splitFromItemId) {
      return throwError(() => new Error('No se encontró el item original.'));
    }

    const sourceItemId = firstChild.splitFromItemId;

    return this.publicApi
      .splitItem(splitId, sourceItemId, {
        groups: unsavedChildren.map((child) => ({
          name: child.description,
          quantity: child.quantity,
          participantIds: child.suggestedParticipantIds ?? [],
        })),
      })
      .pipe(
        tap((createdChildren) => {
          const updatedItems = this.draft.items().map((item) => {
            if (!item.isSplitChild || item.backendId) {
              return item;
            }

            const index = unsavedChildren.findIndex((child) => child.id === item.id);
            const backendItem = createdChildren[index];

            if (!backendItem) return item;

            return {
              ...item,
              backendId: backendItem.id,
              totalPrice: backendItem.totalPrice,
            };
          });

          this.draft.setItems(updatedItems);
        }),
      );
  }

  private saveAssignmentsAndLoadSummaryInternal(
    participantIds: string[] = [],
  ): Observable<BillSplitSummaryResponseDto> {
    const splitId = this.draft.splitId();

    if (!splitId) {
      return throwError(() => new Error('No split id'));
    }

    const unsavedSplitItems = this.draft
      .items()
      .filter((item) => item.isSplitChild && !item.backendId);

    if (unsavedSplitItems.length) {
      return throwError(
        () =>
          new Error('Hay ítems fraccionados que todavía no fueron guardados en la base de datos.'),
      );
    }

    const currentItemIds = new Set(
      this.draft.items().flatMap((item) => [item.id, item.backendId].filter(Boolean) as string[]),
    );

    const validAssignments = this.draft
      .assignments()
      .filter((assignment) => currentItemIds.has(assignment.itemId))
      .filter((assignment) => {
        if (assignment.splitMode === 'BY_QUANTITY') {
          return Object.values(assignment.customQuantities ?? {}).some((q) => q > 0);
        }

        return (assignment.participantIds?.length ?? 0) > 0;
      });

    const request: ReplaceBillSplitItemAssignmentsBulkRequestDto = {
      items: validAssignments.map((assignment) => {
        const item = this.draft
          .items()
          .find((i) => i.id === assignment.itemId || i.backendId === assignment.itemId);

        if (!item) {
          throw new Error(`Item no encontrado: ${assignment.itemId}`);
        }

        if (assignment.splitMode === 'BY_QUANTITY') {
          return {
            itemId: item.backendId ?? item.id,
            assignments: Object.entries(assignment.customQuantities ?? {})
              .filter(([, quantity]) => quantity > 0)
              .map(([participantId, quantity]) => ({
                participantId,
                quantity,
              })),
          };
        }

        const count = assignment.participantIds.length;
        const percentage = Number((100 / count).toFixed(4));

        return {
          itemId: item.backendId ?? item.id,
          assignments: assignment.participantIds.map((participantId) => ({
            participantId,
            percentage,
          })),
        };
      }),
    };

    if (request.items.length === 0) {
      return throwError(() => new Error('No hay asignaciones válidas para enviar.'));
    }

    return this.publicApi.replaceAssignmentsBulk(splitId, request).pipe(
      switchMap(() => this.saveAdjustments(participantIds)),
      switchMap(() => this.refreshSummary()),
      tap(() => {
        this.draft.setStep(4);
        this.storePreview();
      }),
    );
  }
}
